import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { cn } from '@utils/cn';
import { use24h } from '@utils/consts';
import { fromParts12, getMinuteOptions, pad, toParts } from './helpers';
import { TimePickerProps } from './types';

const HOURS_12 = Array.from({ length: 12 }, (_, i) => i + 1);
const HOURS_24 = Array.from({ length: 24 }, (_, i) => i);

const fieldClass =
  'relative flex h-9 min-w-[52px] items-center justify-center rounded-md bg-white px-2.5 max-[899px]:h-11 max-[899px]:rounded-lg max-[899px]:px-3 shadow-[inset_0_0_0_1px_theme(colors.neutral.300)] transition-shadow hover:shadow-[inset_0_0_0_1px_theme(colors.neutral.400)] focus-within:shadow-[inset_0_0_0_1.5px_theme(colors.primary.500),0_0_0_3px_rgba(22,163,74,.28)] max-[899px]:min-w-0 max-[899px]:flex-1';

const selectClass =
  'absolute inset-0 h-full w-full cursor-pointer appearance-none border-0 opacity-0 text-base disabled:cursor-not-allowed';

export const TimePicker = ({ id, label, value, onChange, disabled }: TimePickerProps) => {
  const { t } = useTranslation('app', { keyPrefix: 'scheduling.time' });
  const parts = toParts(value ?? 0);
  const empty = value === undefined;

  const setHours = (hours: number) => {
    if (use24h) onChange?.(hours * 60 + parts.minutes);
    else onChange?.(fromParts12(hours, parts.minutes, parts.period));
  };
  const setMinutes = (minutes: number) => onChange?.(parts.hours * 60 + minutes);
  const setPeriod = (period: 'AM' | 'PM') =>
    onChange?.(fromParts12(parts.hours12, parts.minutes, period));

  const valueClass = cn(
    'pointer-events-none flex items-center gap-1 text-sm font-semibold tabular-nums text-neutral-800 max-[899px]:gap-1.5 max-[899px]:text-base',
    (empty || disabled) && 'font-medium text-neutral-400',
  );

  return (
    <div className="flex items-center gap-2 rounded-lg bg-neutral-50 py-1 pl-2.5 pr-1 max-[899px]:grid max-[899px]:grid-cols-[34px_minmax(0,1fr)] max-[899px]:bg-transparent max-[899px]:p-0">
      <span id={`${id}-label`} className="text-sm font-semibold text-neutral-600">
        {label}
      </span>
      <div
        role="group"
        aria-labelledby={`${id}-label`}
        className="flex items-center gap-1 max-[899px]:gap-1.5">
        <span className={fieldClass}>
          <span className={valueClass} aria-hidden>
            {use24h ? pad(parts.hours) : parts.hours12}
            <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2.6} />
          </span>
          <select
            id={`${id}-hours`}
            aria-label={t('hour', { label })}
            className={selectClass}
            value={use24h ? parts.hours : parts.hours12}
            disabled={disabled}
            onChange={(e) => setHours(Number(e.currentTarget.value))}>
            {(use24h ? HOURS_24 : HOURS_12).map((hour) => (
              <option key={hour} value={hour}>
                {use24h ? pad(hour) : hour}
              </option>
            ))}
          </select>
        </span>
        <span className="text-base font-bold text-neutral-500 max-[899px]:text-lg" aria-hidden>
          :
        </span>
        <span className={fieldClass}>
          <span className={valueClass} aria-hidden>
            {pad(parts.minutes)}
            <ChevronDown className="h-3 w-3 shrink-0" strokeWidth={2.6} />
          </span>
          <select
            id={`${id}-minutes`}
            aria-label={t('minutes', { label })}
            className={selectClass}
            value={parts.minutes}
            disabled={disabled}
            onChange={(e) => setMinutes(Number(e.currentTarget.value))}>
            {getMinuteOptions(parts.minutes).map((minute) => (
              <option key={minute} value={minute}>
                {pad(minute)}
              </option>
            ))}
          </select>
        </span>
        {!use24h && (
          <span
            role="radiogroup"
            aria-label={t('period', { label })}
            className="flex h-9 rounded-md bg-white px-0.5 shadow-[inset_0_0_0_1px_theme(colors.neutral.300)] max-[899px]:h-11 max-[899px]:rounded-lg max-[899px]:px-1">
            {(['AM', 'PM'] as const).map((period) => {
              const selected = parts.period === period;
              return (
                <button
                  key={period}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  disabled={disabled}
                  onClick={() => !selected && setPeriod(period)}
                  className={cn(
                    'relative isolate h-9 w-10 text-xs font-semibold tracking-wide text-neutral-500 before:absolute before:inset-x-0 before:inset-y-[3px] before:-z-10 before:rounded max-[899px]:h-11 max-[899px]:w-11 max-[899px]:text-[13px] max-[899px]:before:inset-y-1 before:transition-colors hover:before:bg-neutral-50 disabled:cursor-not-allowed',
                    selected &&
                      'font-bold text-white before:bg-primary-500 hover:before:bg-primary-500',
                    selected &&
                      (empty || disabled) &&
                      'text-neutral-700 before:bg-neutral-100 hover:before:bg-neutral-100',
                  )}>
                  {period}
                </button>
              );
            })}
          </span>
        )}
      </div>
    </div>
  );
};
